/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { useEffect } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useWatch } from 'react-hook-form'

import { cn } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { NativeSelect } from '../../ui/native-select'

export type DateFormFieldProps = {
  dayProps?: Omit<ComponentProps<typeof NativeSelect>, 'size'>
  endYear?: number
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  labelEnd?: ReactNode
  monthProps?: Omit<ComponentProps<typeof NativeSelect>, 'size'>
  name: string
  required?: boolean
  startYear?: number
  validation?: RegisterOptions
  yearProps?: Omit<ComponentProps<typeof NativeSelect>, 'size'>
}

export function DateFormField({ ...props }: DateFormFieldProps) {
  const { formState, setValue, getValues, register, control } = useFormContext()

  const {
    name,
    validation,
    dayProps,
    monthProps,
    yearProps,
    startYear = 1900,
    endYear = new Date().getFullYear(),
    ...rest
  } = props

  const { errors } = formState

  if (validation) {
    register(name, validation)
  }

  const { ref: dayRef, ...dayRegister } = register(`${name}_day`)
  const { ref: monthRef, ...monthRegister } = register(`${name}_month`)
  const { ref: yearRef, ...yearRegister } = register(`${name}_year`)

  const dayValue = useWatch({ control, name: `${name}_day` })
  const monthValue = useWatch({ control, name: `${name}_month` })
  const yearValue = useWatch({ control, name: `${name}_year` })

  const days = Array.from({ length: 31 }, (_, i) => i + 1)
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ]
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i)

  useEffect(() => {
    const initialDate = getValues(name)
    if (initialDate) {
      let dateObj: Date | null = null

      if (initialDate instanceof Date) {
        dateObj = initialDate
      } else if (typeof initialDate === 'string' && initialDate) {
        dateObj = new Date(initialDate)
        if (Number.isNaN(dateObj.getTime())) {
          dateObj = null
        }
      }

      if (dateObj) {
        const day = dateObj.getDate()
        const month = dateObj.getMonth() + 1
        const year = dateObj.getFullYear()

        setValue(`${name}_day`, day, { shouldValidate: false })
        setValue(`${name}_month`, month, { shouldValidate: false })
        setValue(`${name}_year`, year, { shouldValidate: false })
      }
    }
  }, [name, setValue, getValues])

  useEffect(() => {
    if (dayValue && monthValue && yearValue) {
      const day = Number(dayValue)
      const month = Number(monthValue)
      const year = Number(yearValue)

      const dateObj = new Date(year, month - 1, day)

      if (dateObj.getDate() === day && dateObj.getMonth() === month - 1 && dateObj.getFullYear() === year) {
        setValue(name, dateObj, { shouldValidate: true })
      } else {
        setValue(name, null, { shouldValidate: true })
      }
    } else {
      setValue(name, null, { shouldValidate: false })
    }
  }, [dayValue, monthValue, yearValue, name, setValue])

  const hasError = !!(errors[name] || errors[`${name}_day`] || errors[`${name}_month`] || errors[`${name}_year`])

  return (
    <Field {...rest.fieldProps} data-invalid={hasError}>
      {props.label !== undefined && (
        <div className={cn('flex items-center gap-1', props.labelEnd != null && 'w-full')}>
          <FieldLabel className="flex items-center gap-1">
            {props.label}
            {props.required ? (
              <span aria-hidden className="text-destructive">
                *
              </span>
            ) : null}
          </FieldLabel>
          {props.labelEnd}
        </div>
      )}

      <div className="grid w-full grid-cols-[3fr_2fr_2fr] gap-2">
        <NativeSelect
          {...monthRegister}
          {...monthProps}
          aria-invalid={hasError}
          className="w-full"
          id={`${name}_month`}
          ref={monthRef}
          suppressHydrationWarning
        >
          <option value="">Month</option>
          {months.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </NativeSelect>

        <NativeSelect
          {...dayRegister}
          {...dayProps}
          aria-invalid={hasError}
          className="w-full"
          id={`${name}_day`}
          ref={dayRef}
          suppressHydrationWarning
        >
          <option value="">Day</option>
          {days.map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </NativeSelect>

        <NativeSelect
          {...yearRegister}
          {...yearProps}
          aria-invalid={hasError}
          className="w-full"
          id={`${name}_year`}
          ref={yearRef}
          suppressHydrationWarning
        >
          <option value="">Year</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </NativeSelect>
      </div>
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {(errors[name] || errors[`${name}_day`] || errors[`${name}_month`] || errors[`${name}_year`]) && (
        <FieldError>
          {String(
            errors[name]?.message ||
              errors[`${name}_day`]?.message ||
              errors[`${name}_month`]?.message ||
              errors[`${name}_year`]?.message ||
              'Invalid date'
          )}
        </FieldError>
      )}
    </Field>
  )
}
